// Le registre de veille : ce qu'on a lu, et ce que ça a changé.
//
// Les trois indicateurs — 23 légal, 24 métiers, 25 pédagogique — disent tous
// « et en exploite les enseignements ». L'auditeur ne demande pas la
// procédure : il demande ce registre. Et la procédure de la maison le promet
// déjà noir sur blanc, alors qu'il n'existait pas.
//
// Trois cartes en tête, une par domaine, parce qu'un domaine muet fait tomber
// son indicateur quoi que disent les deux autres. Le reste ne calcule rien :
// l'état, les manques et le bilan viennent du serveur — deux calculs du même
// état finiraient par ne pas peindre la même ligne en orange.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Download, Search, ExternalLink, Check, Trash2, Pencil,
  Scale, Briefcase, Lightbulb, AlertTriangle, X,
} from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const PICTO = { LEGALE: Scale, METIERS: Briefcase, PEDAGOGIQUE: Lightbulb };

const TEINTE_STATUT = {
  a_exploiter: 'bg-[#FEF3C7] text-[#92400E]',
  en_cours: 'bg-[#DBEAFE] text-[#1E40AF]',
  exploitee: 'bg-[#D1FAE5] text-[#065F46]',
};

const VIDE = {
  domaine_code: 'LEGALE', date_veille: '', titre: '', source: '', lien: '',
  contenu: '', impact: '', sans_impact: false, action: '', action_faite_le: '',
  responsable: '',
};

const Champ = ({ label, aide, children }) => (
  <div>
    <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">{label}</label>
    {children}
    {aide && <div className="text-[11px] text-[#8E8FA5] mt-1">{aide}</div>}
  </div>
);

const classeInput = 'w-full h-9 px-3 rounded-lg border border-[#E6E7F0] text-[13px] focus:border-[#4F46E5] outline-none';
const classeZone = 'w-full px-3 py-2 rounded-lg border border-[#E6E7F0] text-[13px] focus:border-[#4F46E5] outline-none';

export default function RegistreVeille() {
  const [lignes, setLignes] = useState([]);
  const [bilan, setBilan] = useState(null);
  const [refs, setRefs] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [domaine, setDomaine] = useState(null);
  const [vue, setVue] = useState(null);
  const [form, setForm] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const charger = () => api.get('/veille')
    .then(({ data }) => { setLignes(data.veille || []); setBilan(data.bilan); })
    .catch(() => {});

  useEffect(() => {
    charger();
    api.get('/veille/referentiels').then(({ data }) => setRefs(data)).catch(() => {});
  }, []);

  const visibles = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return (lignes || []).filter((v) => {
      if (domaine && v.domaine_code !== domaine) return false;
      if (vue && v.statut !== vue) return false;
      if (!q) return true;
      return [v.titre, v.source, v.impact, v.action, v.contenu, v.id]
        .some((t) => (t || '').toLowerCase().includes(q));
    });
  }, [lignes, recherche, domaine, vue]);

  const exporter = async () => {
    try {
      const { data } = await api.get('/veille/export', { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registre-veille-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Export impossible', description: 'Réessayez dans un instant.' });
    }
  };

  const enregistrer = async () => {
    setEnCours(true);
    try {
      const corps = { ...form };
      delete corps.id; delete corps.statut; delete corps.statut_libelle;
      delete corps.manques; delete corps.domaine_libelle; delete corps.indicateur;
      delete corps.cree_le; delete corps.cree_par; delete corps.modifie_le; delete corps.org_id;
      if (!corps.action_faite_le) corps.action_faite_le = null;
      if (!corps.date_veille) corps.date_veille = null;
      if (form.id) await api.put(`/veille/${form.id}`, corps);
      else await api.post('/veille', corps);
      setForm(null);
      await charger();
      toast({ title: form.id ? 'Veille mise à jour' : 'Veille consignée' });
    } catch (e) {
      toast({ title: 'Enregistrement impossible',
        description: e?.response?.data?.detail || 'Réessayez dans un instant.' });
    } finally {
      setEnCours(false);
    }
  };

  const noterFaite = async (v) => {
    try {
      await api.post(`/veille/${v.id}/action-faite`, {});
      await charger();
      toast({ title: 'Action notée faite' });
    } catch (e) {
      toast({ title: 'Impossible', description: e?.response?.data?.detail || '' });
    }
  };

  const retirer = async (v) => {
    if (!window.confirm(`Retirer « ${v.titre || v.id} » du registre ?`)) return;
    try {
      await api.delete(`/veille/${v.id}`);
      await charger();
    } catch {
      toast({ title: 'Suppression impossible' });
    }
  };

  const domainesRef = refs?.domaines || [];
  const couvre = (code) => (domainesRef.find((d) => d.code === code) || {}).couvre;

  return (
    <div className="space-y-4" data-testid="registre-veille">
      {/* Titre + gestes */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[15.5px] font-semibold text-[#1A1B2E]"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Registre de veille
          </h3>
          <p className="text-[12.5px] text-[#8E8FA5]">
            Indicateurs 23, 24 et 25 — ce qu'on a lu, l'impact pour l'organisme,
            et ce qu'on en a fait. C'est ce fichier qu'un auditeur demande.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="veille-export"
            onClick={exporter}
            className="h-9 px-3 rounded-lg border border-[#E6E7F0] text-[12.5px] font-semibold text-[#4A4B63] flex items-center gap-1.5 hover:border-[#C7D2FE]"
          >
            <Download className="w-3.5 h-3.5" /> Exporter
          </button>
          <button
            data-testid="veille-nouvelle"
            onClick={() => setForm({ ...VIDE })}
            className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Consigner une veille
          </button>
        </div>
      </div>

      {/* Une carte par domaine : c'est un domaine muet qui fait tomber son
          indicateur, et c'est la premiere chose qu'un audit regarde. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(bilan?.par_domaine || []).map((d) => {
          const Icone = PICTO[d.code] || Scale;
          const choisi = domaine === d.code;
          return (
            <button
              key={d.code}
              data-testid={`veille-domaine-${d.code}`}
              onClick={() => setDomaine(choisi ? null : d.code)}
              className={`text-left p-3 rounded-xl border transition-colors ${
                choisi ? 'border-[#4F46E5] bg-[#F5F3FF]'
                  : d.muet ? 'border-[#FDE68A] bg-[#FFFBEB] hover:border-[#F59E0B]'
                    : d.en_retard ? 'border-[#FECACA] bg-[#FEF2F2] hover:border-[#EF4444]'
                      : 'border-[#EFF0F6] bg-[#FAFAFE] hover:border-[#C7D2FE]'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <Icone className="w-3.5 h-3.5 text-[#6B6C85]" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6B6C85]">
                  Indicateur {d.indicateur}
                </span>
                <span className="ml-auto text-[15px] font-bold text-[#1A1B2E]">{d.total}</span>
              </div>
              <div className="text-[12.5px] font-semibold text-[#1A1B2E] mt-1">{d.libelle}</div>
              {d.muet ? (
                <div className="text-[11.5px] font-semibold text-[#92400E] mt-0.5">
                  Jamais alimenté — l'indicateur tombe
                </div>
              ) : (
                <div className={`text-[11.5px] mt-0.5 ${d.en_retard ? 'font-semibold text-[#B91C1C]' : 'text-[#8E8FA5]'}`}>
                  Dernière entrée il y a {d.jours} jour{d.jours > 1 ? 's' : ''}
                  {d.en_retard && ` — au-delà des ${bilan.jours_attendus} jours de votre procédure`}
                </div>
              )}
              {couvre(d.code) && (
                <div className="text-[11px] text-[#A0A1B5] mt-1 leading-snug">{couvre(d.code)}</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Les trois etats, et ils filtrent. */}
      {bilan && bilan.total > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {[['a_exploiter', 'À exploiter', bilan.a_exploiter],
            ['en_cours', 'Action en cours', bilan.en_cours],
            ['exploitee', 'Exploitées', bilan.exploitees]].map(([code, libelle, n]) => (
            <button
              key={code}
              data-testid={`veille-vue-${code}`}
              onClick={() => setVue(vue === code ? null : code)}
              className={`h-8 px-2.5 rounded-lg text-[12px] font-semibold border ${
                vue === code ? 'border-[#4F46E5] text-[#4F46E5] bg-[#F5F3FF]'
                  : 'border-[#EFF0F6] text-[#6B6C85] hover:border-[#C7D2FE]'
              }`}
            >
              {libelle} · {n}
            </button>
          ))}
          <div className="relative ml-auto">
            <Search className="w-3.5 h-3.5 text-[#A0A1B5] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Chercher une source, un impact…"
              className="h-8 pl-8 pr-3 rounded-lg border border-[#E6E7F0] text-[12.5px] w-56 outline-none focus:border-[#4F46E5]"
            />
          </div>
        </div>
      )}

      {/* Le registre */}
      <div className="soft-card overflow-hidden">
        {visibles.length === 0 ? (
          <div className="p-6 text-center">
            <div className="text-[13px] text-[#6B6C85]">
              {lignes.length === 0
                ? 'Rien n’est encore consigné. Votre procédure de veille annonce une veille mensuelle : la première entrée est celle qui la rend vraie.'
                : 'Aucune entrée ne correspond à ce filtre.'}
            </div>
          </div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-[#EFF0F6] text-[11px] uppercase tracking-wider text-[#8E8FA5]">
                <th className="text-left px-3 py-2 font-semibold">Date</th>
                <th className="text-left px-3 py-2 font-semibold">Information</th>
                <th className="text-left px-3 py-2 font-semibold">Impact · action</th>
                <th className="text-left px-3 py-2 font-semibold">Statut</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {visibles.map((v) => {
                const Icone = PICTO[v.domaine_code] || Scale;
                return (
                  <tr key={v.id} className="border-b border-[#F6F7FB] last:border-0 align-top hover:bg-[#FAFAFE] group">
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="text-[#1A1B2E] font-medium">
                        {v.date_veille ? new Date(v.date_veille).toLocaleDateString('fr-FR') : '—'}
                      </div>
                      <div className="flex items-center gap-1 text-[11px] text-[#8E8FA5] mt-0.5">
                        <Icone className="w-3 h-3" /> ind. {v.indicateur}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-[#1A1B2E]">{v.titre || '—'}</div>
                      <div className="text-[11.5px] text-[#8E8FA5] flex items-center gap-1.5 flex-wrap">
                        {v.source || <span className="text-[#B45309] font-semibold">source à renseigner</span>}
                        {v.lien && (
                          <a href={v.lien} target="_blank" rel="noreferrer"
                             className="text-[#4F46E5] inline-flex items-center gap-0.5 hover:underline">
                            le lien <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                      {v.contenu && (
                        <div className="text-[11.5px] text-[#6B6C85] mt-1 line-clamp-2">{v.contenu}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-[20rem]">
                      {v.sans_impact ? (
                        <div className="text-[#065F46] font-medium">Aucun impact pour l’organisme</div>
                      ) : v.impact ? (
                        <>
                          <div className="text-[#1A1B2E]">{v.impact}</div>
                          {v.action && (
                            <div className="text-[11.5px] text-[#6B6C85] mt-0.5">
                              → {v.action}
                              {v.action_faite_le && ` · fait le ${new Date(v.action_faite_le).toLocaleDateString('fr-FR')}`}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-[#B45309] text-[11.5px] font-semibold">
                          Impact à écrire — ou cocher « aucun impact »
                        </div>
                      )}
                      {(v.manques || []).length > 0 && (
                        <div className="text-[11px] text-[#92400E] mt-1 flex items-start gap-1">
                          <AlertTriangle className="w-3 h-3 mt-[1px] shrink-0" />
                          <span>{v.manques.map((m) => m.libelle).join(' · ')}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${TEINTE_STATUT[v.statut] || ''}`}>
                        {v.statut_libelle}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {v.action && !v.action_faite_le && (
                          <button
                            data-testid={`veille-faite-${v.id}`}
                            onClick={() => noterFaite(v)}
                            title="Noter l'action faite aujourd'hui"
                            className="h-7 px-2 rounded-lg border border-[#A7F3D0] text-[11.5px] font-semibold text-[#065F46] hover:bg-[#ECFDF5] flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" /> Faite
                          </button>
                        )}
                        <button
                          onClick={() => setForm({ ...VIDE, ...v,
                            date_veille: (v.date_veille || '').slice(0, 10),
                            action_faite_le: (v.action_faite_le || '').slice(0, 10) })}
                          title="Corriger"
                          className="h-7 w-7 rounded-lg border border-[#E6E7F0] text-[#6B6C85] hover:border-[#C7D2FE] flex items-center justify-center"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => retirer(v)}
                          title="Retirer du registre"
                          className="h-7 w-7 rounded-lg border border-[#E6E7F0] text-[#B91C1C] hover:border-[#FECACA] flex items-center justify-center"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* La saisie */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[16px]">
              {form?.id ? 'Corriger une veille' : 'Consigner une veille'}
            </DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <Champ label="Quel domaine">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {domainesRef.map((d) => {
                    const Icone = PICTO[d.code] || Scale;
                    const choisi = form.domaine_code === d.code;
                    return (
                      <button
                        key={d.code}
                        type="button"
                        data-testid={`veille-form-domaine-${d.code}`}
                        onClick={() => setForm({ ...form, domaine_code: d.code })}
                        className={`text-left p-2.5 rounded-lg border text-[12px] ${
                          choisi ? 'border-[#4F46E5] bg-[#F5F3FF]' : 'border-[#E6E7F0] hover:border-[#C7D2FE]'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-semibold text-[#1A1B2E]">
                          <Icone className="w-3.5 h-3.5" /> ind. {d.indicateur}
                        </div>
                        <div className="text-[11.5px] text-[#6B6C85] mt-0.5">{d.libelle}</div>
                      </button>
                    );
                  })}
                </div>
              </Champ>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Champ label="Date" aide="Vide : aujourd'hui">
                  <input type="date" value={form.date_veille || ''} className={classeInput}
                         onChange={(e) => setForm({ ...form, date_veille: e.target.value })} />
                </Champ>
                <div className="sm:col-span-2">
                  <Champ label="La source" aide="« JO du 4 août 2026 », « Centre Inffo », « DREETS » — c'est elle qu'un auditeur demande à voir.">
                    <input value={form.source} className={classeInput}
                           placeholder="D'où vient l'information"
                           onChange={(e) => setForm({ ...form, source: e.target.value })} />
                  </Champ>
                </div>
              </div>

              <Champ label="L'information, en une phrase">
                <input value={form.titre} className={classeInput}
                       placeholder="Décret 2026-728 : le référentiel passe à 33 indicateurs"
                       onChange={(e) => setForm({ ...form, titre: e.target.value })} />
              </Champ>

              <Champ label="Le lien (facultatif)">
                <input value={form.lien} className={classeInput} placeholder="https://…"
                       onChange={(e) => setForm({ ...form, lien: e.target.value })} />
              </Champ>

              <Champ label="Ce qu'elle dit (facultatif)">
                <textarea rows={3} value={form.contenu} className={classeZone}
                          onChange={(e) => setForm({ ...form, contenu: e.target.value })} />
              </Champ>

              {/* « Lu, aucun impact » est une decision ; le silence n'en est pas
                  une. Les deux ne peuvent pas coexister, et c'est le serveur qui
                  efface — l'ecran ne fait que ne pas les proposer ensemble. */}
              <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3 space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="veille-sans-impact"
                    checked={!!form.sans_impact}
                    onChange={(e) => setForm({ ...form, sans_impact: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-[12.5px] font-semibold text-[#1A1B2E]">
                      Lu, aucun impact pour l'organisme
                    </span>
                    <span className="block text-[11px] text-[#8E8FA5]">
                      Un auditeur accepte cette réponse — à condition qu'elle soit
                      écrite. Ce qu'il n'accepte pas, c'est une ligne posée sans suite.
                    </span>
                  </span>
                </label>

                {!form.sans_impact && (
                  <>
                    <Champ label="L'impact pour nous"
                           aide="Les trois indicateurs disent « et en exploite les enseignements » : c'est ici que ça se lit.">
                      <textarea rows={2} value={form.impact} className={classeZone}
                                placeholder="Nos fiches programme devront dire la méthode de calcul du taux publié"
                                onChange={(e) => setForm({ ...form, impact: e.target.value })} />
                    </Champ>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2">
                        <Champ label="Ce qu'on en fait">
                          <input value={form.action} className={classeInput}
                                 placeholder="Reprendre les 41 fiches avant le 1er novembre"
                                 onChange={(e) => setForm({ ...form, action: e.target.value })} />
                        </Champ>
                      </div>
                      <Champ label="Fait le">
                        <input type="date" value={form.action_faite_le || ''} className={classeInput}
                               onChange={(e) => setForm({ ...form, action_faite_le: e.target.value })} />
                      </Champ>
                    </div>
                  </>
                )}
              </div>

              <Champ label="Qui s'en charge" aide="Vide : vous.">
                <input value={form.responsable} className={classeInput}
                       onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
              </Champ>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => setForm(null)}
                        className="h-9 px-3 rounded-lg border border-[#E6E7F0] text-[12.5px] font-semibold text-[#6B6C85] flex items-center gap-1.5">
                  <X className="w-3.5 h-3.5" /> Annuler
                </button>
                <button
                  data-testid="veille-enregistrer"
                  onClick={enregistrer}
                  disabled={enCours}
                  className="h-9 px-4 rounded-lg brand-gradient text-white text-[12.5px] font-semibold disabled:opacity-60"
                >
                  {enCours ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
